import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Button, Row, Col, Table, Radio, Space, Typography, Tooltip } from 'antd'
import { PlusOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons'

import { useUser } from '../../../../providers/UserProvider'
import MedicalEntityModal from '../../Modals/MedicalEntityModal'
import UploadSpecialRelease from '../UploadSpecialRelease'
import showConfirmDialog from '../../Modals/DeleteConfirm'
import useDispatchHttp from '../../../../hooks/dispatchHttpHandler'
import {
  deleteSpecialReleaseForm,
  updateSpecialReleaseForm,
  fetchFormById,
  selectFacilityById,
  fetchFacilityById,
} from '../../../../state/modules/facilities'
import { formatDate } from '../../../../utils/helpers'
import { downloadFileByUrl } from '../../../../utils/functions'
import { uploadSpecialReleaseFileSchema } from '../../../../schema/uploadSpecialReleaseFile'

const SpecialReleasesList = ({ facilityId, onSubmit, onClose }) => {
  const dispatchHttp = useDispatchHttp()
  const { isUserActionAllowed } = useUser()
  const facilityData = useSelector(state => selectFacilityById(state, facilityId))
  const [selectedFormId, setSelectedFormId] = useState(null)
  const isMounted = useRef(true)

  const canUpload = isUserActionAllowed('add_authorizationform')
  const canDelete = isUserActionAllowed('delete_authorizationform')
  const canDownload = isUserActionAllowed('download_authorizationform')
  const canChangeCurrent = isUserActionAllowed('change_authorizationform')

  const originalCurrentFormId = useMemo(
    () => facilityData?.special_release_forms?.find(form => form.is_current)?.id,
    [facilityData?.special_release_forms]
  )

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    setSelectedFormId(originalCurrentFormId)
  }, [originalCurrentFormId])

  const handleDelete = useCallback(
    async formId => {
      await dispatchHttp(
        deleteSpecialReleaseForm({ formId, facilityId }),
        'Form has been successfully deleted'
      )

      await dispatchHttp(fetchFacilityById(facilityId))
    },
    [dispatchHttp, facilityId]
  )

  const handleDownload = useCallback(
    async formId => {
      const resp = await dispatchHttp(fetchFormById({ formId, id: facilityId }))

      downloadFileByUrl(resp?.changes?.link, resp?.changes?.attach_name)
    },
    [dispatchHttp, facilityId]
  )

  const handleSaveChanges = useCallback(async () => {
    if (selectedFormId === originalCurrentFormId) {
      return
    }

    await dispatchHttp(
      updateSpecialReleaseForm(selectedFormId),
      'Current form has been successfully updated'
    )

    if (isMounted.current) {
      await dispatchHttp(fetchFacilityById(facilityId))
    }
  }, [dispatchHttp, facilityId, selectedFormId, originalCurrentFormId])

  const columns = useMemo(() => {
    const tableColumns = [
      {
        title: 'File Name',
        dataIndex: 'attach_name',
        key: 'attach_name',
        render: text => (
          <span className="special-release-form-list__file-name">{text}</span>
        ),
      },
      {
        title: 'Uploaded',
        dataIndex: 'created_at',
        key: 'created_at',
        render: text => formatDate(text),
      },
      {
        title: 'By',
        dataIndex: 'editor',
        key: 'editor',
        render: editor => (editor ? `${editor.firstname} ${editor.lastname}` : 'N/A'),
      },
    ]

    if (canDownload || canDelete) {
      tableColumns.push({
        title: 'Actions',
        key: 'actions',
        className: 'action-column',
        width: 154,
        render: (_, record) => (
          <Space size="middle">
            {canDownload && (
              <Typography.Link onClick={() => handleDownload(record.id)}>
                <Space>
                  <DownloadOutlined />
                  Download
                </Space>
              </Typography.Link>
            )}
            {canDelete && (
              <Tooltip title="Delete form">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    showConfirmDialog({
                      title: `Do you want to delete ${record.attach_name}?`,
                      data: record.id,
                      handleConfirm: handleDelete,
                    })
                  }
                />
              </Tooltip>
            )}
          </Space>
        ),
      })
    }

    if (canChangeCurrent) {
      tableColumns.unshift({
        key: 'radio',
        className: 'radio-column',
        render: (_, record) => <Radio value={record.id} />,
      })
    }
    return tableColumns
  }, [canDelete, canChangeCurrent, canDownload, handleDelete, handleDownload])

  const TableContent = () => (
    <Table
      columns={columns}
      dataSource={facilityData?.special_release_forms || []}
      rowKey="id"
      pagination={false}
      style={{ width: '100%' }}
    />
  )

  return (
    <div className="form-list special-release-form-list">
      <Row gutter={[24, 16]}>
        <Col span={24} style={{ textAlign: 'right' }}>
          {canUpload && (
            <MedicalEntityModal
              title="Attach Special Release Form"
              className="special-release-form-modal"
              width={580}
              trigger={
                <Button
                  type="primary"
                  ghost
                  icon={<PlusOutlined className="form-list__add-icon" />}
                >
                  Upload
                </Button>
              }
              form={
                <UploadSpecialRelease
                  validationSchema={uploadSpecialReleaseFileSchema}
                  onSubmit={onSubmit}
                  initialValues={{ special_auth: 0, e_signature: 0, attachment: null }}
                />
              }
            />
          )}
        </Col>

        <Col span={24} style={{ lineHeight: 0 }}>
          {canChangeCurrent ? (
            <Radio.Group
              onChange={e => setSelectedFormId(e.target.value)}
              value={selectedFormId}
              style={{ width: '100%' }}
            >
              <TableContent />
            </Radio.Group>
          ) : (
            <TableContent />
          )}
        </Col>

        <Col span={24}>
          <Row justify="space-between">
            <Col>
              <Button onClick={onClose}>Close</Button>
            </Col>
            <Col>
              {canChangeCurrent && (
                <Button
                  type="primary"
                  onClick={handleSaveChanges}
                  disabled={selectedFormId === originalCurrentFormId}
                >
                  Save Changes
                </Button>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  )
}

export default SpecialReleasesList
